import { KeyboardReturn, Search, Warning } from '@mui/icons-material';
import { TextField } from '@mui/material';
import { grey } from '@mui/material/colors';
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '~/components/Button';
import { FaviconImage } from '~/components/FaviconImage';
import { useDebounce } from '~/hooks/useDebounce';
import useRouter from '~/hooks/useRouter';
import { getHostname } from '~/scripts/parseDomain';

export const DomainSearchForm = ({}: {}) => {
  const router = useRouter();
  const [debouncedInput, searchInput, setSearchInput] = useDebounce('', 200);
  const searchHostname = useMemo(() => {
    return getHostname(debouncedInput);
  }, [debouncedInput]);
  const searchInputError =
    debouncedInput && !searchHostname ? 'Invalid domain name' : '';

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (debouncedInput !== searchInput) {
        return;
      }
      if (!searchInputError) {
        router.push(`/domains/${searchHostname}`);
      }
    },
    [debouncedInput, searchInput, searchHostname, searchInputError, router]
  );

  const logo = useMemo(() => {
    if (searchInputError) {
      return <Warning sx={{ mr: 1 }} color="warning" />;
    }
    if (!searchHostname) {
      return <Search sx={{ mr: 1 }} />;
    }

    return (
      <FaviconImage
        domain={searchHostname}
        height={30}
        width={30}
        sx={{
          mr: 1,
          border: `solid 1px ${grey[300]}`,
          borderRadius: 1,
        }}
      />
    );
  }, [searchHostname, searchInputError]);

  const [focused, setFocused] = useState(false);
  const shrink = useMemo(() => {
    return !!focused || !!searchInput;
  }, [focused, searchInput]);
  return (
    <form onSubmit={handleSubmit}>
      <TextField
        variant="outlined"
        label={focused ? 'Domain Search' : 'Example: domain.com'}
        fullWidth
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        error={!!searchInputError}
        helperText={searchInputError}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        InputProps={{
          startAdornment: logo,
          endAdornment: searchHostname && (
            <Button
              type="submit"
              variant="outlined"
              size="small"
              sx={{ px: 3 }}
              startIcon={<KeyboardReturn />}>
              Submit
            </Button>
          ),
        }}
        InputLabelProps={{
          shrink: shrink,
          style: {
            paddingLeft: shrink ? 0 : 30,
          },
        }}
      />
    </form>
  );
};
